import React, { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { App, Button, Dropdown, Input } from 'antd';

import EventFormElement from '../events/EventFormElement';
import FieldHelp, { elementHelpText } from '../common/FieldHelp';
import SortableList, { ReorderButton, moveId } from '../common/SortableList';
import { buildActionMenu } from '../common/actionMenu';
import { ARROW_COUNTERCLOCKWISE, ARROW_DOWN, ARROW_UP, CHEVRON_DOWN, CHEVRON_RIGHT, CLOSE, KEBAB, PLUS } from '../icons';
import { formatValue } from './itemValueDisplay';
import useLinkedEntry, { entryLink } from '../../hooks/useLinkedEntry';
import copyTextToClipboard from '../../utils/clipboard';
import { fieldWidthClass } from '../../utils/elements';
import { GRANT_READ_ONLY_REASON, isSeeOnlyElement } from '../../utils/elementAccess';
import { normalizeId } from '../../utils/ids';
import { isFormulaDriven } from '../../utils/formulaTokens';
import {
	FOLD_ABOVE,
	ENTRY_COLUMN,
	HEADING_COLUMN,
	HEADING_MAX_LENGTH,
	addLabel,
	allowsHeadings,
	atMaxSentence,
	belowMinSentence,
	countEntries,
	countPhrase,
	entriesOf,
	entryKey,
	entryName,
	entrySummary,
	errorsForGroup,
	firstEntryError,
	isGroupWritable,
	isHeading,
	limitsOf,
	limitsSentence,
	newEntry,
	newHeading,
	nounsOf,
	seeOnlyColumnNames,
	visibleColumns,
	withKey,
} from '../../utils/repeatingGroupEntries';

/**
 * A repeating group on the item form (KPR-680; epic KPE-027): the entries of a
 * list, each with the group's own columns. The group is ONE element of its
 * card; its value is the ordered list the API stores
 * (`[{ id, elements } | { id, section }]`), and this component only ever edits
 * that list — the item form sends it (whole, or as single-entry operations).
 *
 * What a person sees, in the platform's own grammar:
 *
 *   - an entry is a bordered card: a head (fold toggle, number, a one-line
 *     summary, a ⋯) over its columns, each column the SAME input an element
 *     of that type gets (EventFormElement), with its own message beside it;
 *   - Add is a secondary button named from the group ("Add ingredient"); at
 *     the maximum it is off and a line beside it says why;
 *   - an empty group invites the first entry;
 *   - removing an entry is a pending edit of the form, so it is undone, not
 *     confirmed: "Removed ingredient 3. Undo.";
 *   - reordering is asked for (a Reorder button, then a grip on every row,
 *     the pointer and the keyboard both; ⋯ gains Move up / Move down), as
 *     everywhere else in the platform (docs/platform-standards.md § 5);
 *   - past `FOLD_ABOVE` entries they start folded to their summary line, and
 *     every entry is memoised, so a group of 100 stays responsive.
 */

const NO_ERRORS = Object.freeze({});
// An entry's menu only needs its order list while reordering, and its own row does not reorder.
const NO_IDS = Object.freeze([]);
const sameErrors = (a, b) => {
	const keys = Object.keys(a);
	return keys.length === Object.keys(b).length && keys.every(key => a[key] === b[key]);
};

// The item's values a group's calculated columns read (KPR-828): the first name
// of every {{reference}} in their formulas. A column's own entry is read from
// the entry, so the item's other names are all this needs.
const FORMULA_REFERENCE = /\{\{\s*([^{}.\s]+)/g;
const formulaReadsOf = (columns) => {
	const names = new Set();
	let any = false;
	columns.forEach((column) => {
		if (!isFormulaDriven(column)) return;
		any = true;
		for (const match of String(column.options.drivenFrom.formula || '').matchAll(FORMULA_REFERENCE)) names.add(match[1]);
	});
	return any ? [...names] : null;
};

// A file column's pick button stands for its (hidden) file input: it is what takes the keyboard.
const FIELD_CONTROLS = 'input, textarea, select, [contenteditable="true"], [role="combobox"], [role="switch"], .file-upload .ant-upload button';
const takesFocus = node => !(node.disabled || node.type === 'hidden' || node.type === 'file' || node.getAttribute('aria-disabled') === 'true');
const firstControl = root => [...(root?.querySelectorAll?.(FIELD_CONTROLS) || [])].find(takesFocus) || null;

const widthOf = column => {
	// A file column is an upload control with its files beneath it: the row.
	if (column.dataType === 'file') return 'full';
	const klass = fieldWidthClass(column);
	if (klass === 'field--full') return 'full';
	if (klass === 'field--num' || klass === 'field--date') return 'small';
	return 'half';
};

/** The entry's ⋯: Copy link; Move up / Move down while reordering; Remove last, red. */
function entryMenu({ entry, name, index, ids, reordering, writable, onMove, onRemove, onCopyLink }) {
	return buildActionMenu([
		{
			key: 'move-up',
			group: 'record',
			icon: <ARROW_UP />,
			label: 'Move up',
			disabled: index <= 0,
			hidden: !writable || !reordering,
			onClick: () => onMove(moveId(ids, entryKey(entry), -1)),
		},
		{
			key: 'move-down',
			group: 'record',
			icon: <ARROW_DOWN />,
			label: 'Move down',
			disabled: index < 0 || index >= ids.length - 1,
			hidden: !writable || !reordering,
			onClick: () => onMove(moveId(ids, entryKey(entry), 1)),
		},
		{
			key: 'copy-link',
			group: 'share',
			glyph: 'share',
			label: 'Copy link to this entry',
			hidden: !entry.id || reordering,
			onClick: () => onCopyLink(entry),
		},
		{
			key: 'remove',
			group: 'destructive',
			glyph: 'remove',
			label: 'Remove entry',
			title: `Remove ${name}`,
			hidden: !writable || reordering,
			onClick: () => onRemove(entry),
		},
	]);
}

function EntryActions({ entry, name, index, ids, reordering, writable, onMove, onRemove, onCopyLink }) {
	const menu = entryMenu({ entry, name, index, ids, reordering, writable, onMove, onRemove, onCopyLink });
	if (!menu) return null;
	return (
		<Dropdown trigger={['click']} placement="bottomRight" menu={menu}>
			<Button type="text" className="item-entry__menu" icon={<KEBAB />} aria-label={`Actions for ${name}`} />
		</Dropdown>
	);
}

/** One entry — a card with its head and its columns. Memoised: typing in one re-renders only it. */
const GroupEntry = memo(function GroupEntry({
	group,
	columns,
	entry,
	index,
	ordinal,
	name,
	open,
	linked,
	errors,
	storedEntry,
	groupContext,
	fileItem,
	writable,
	collectionId,
	cardId,
	cardMeta,
	attachmentsOn,
	zone,
	resolveLookupValue,
	format,
	onToggle,
	onColumn,
	onHeading,
	onMove,
	onRemove,
	onCopyLink,
}) {
	const key = entryKey(entry);
	const bodyId = `item-entry-${useId().replace(/:/g, '')}`;
	const heading = isHeading(entry);
	// The one line the entry folds to — worked out here, so typing in one
	// entry never re-reads the other ninety-nine.
	const summary = useMemo(() => entrySummary(group, entry, format), [group, entry, format]);
	const failing = Object.keys(errors).length > 0;
	const Chevron = open ? CHEVRON_DOWN : CHEVRON_RIGHT;
	const event = useMemo(() => ({ collection_id: collectionId, card_id: cardId, elements: entry.elements || {} }), [collectionId, cardId, entry.elements]);
	const storedElements = storedEntry?.elements || null;
	// What a FILE column needs beyond its entry (KPR-934): the item whose `files`
	// facts describe the stored files, the group the upload names, and this entry.
	const entryFile = useMemo(() => ({ item: fileItem, group: group.name, entryKey: key }), [fileItem, group.name, key]);
	const handleColumn = useCallback((columnName, next) => onColumn(key, columnName, next), [onColumn, key]);
	const idPrefix = `${group.name}-${key}-`;

	return (
		<div
			className={[
				'item-entry',
				heading ? 'item-entry--heading' : '',
				failing ? 'item-entry--invalid' : '',
				linked ? 'item-entry--linked' : '',
			].filter(Boolean).join(' ')}
			data-entry-key={key}
			id={entry.id ? `entry-${entry.id}` : undefined}
		>
			<div className="item-entry__head">
				{heading ? (
					// A heading is one text box: it sits in the head itself, with nothing to fold.
					<div className="item-entry__heading-field">
						<Input
							aria-label="Heading"
							placeholder="Section heading"
							value={entry.section}
							maxLength={HEADING_MAX_LENGTH}
							disabled={!writable}
							status={errors[HEADING_COLUMN] ? 'error' : undefined}
							onChange={event2 => onHeading(key, event2.target.value)}
						/>
						{errors[HEADING_COLUMN] ? <div className="element-field__error" role="alert">{errors[HEADING_COLUMN]}</div> : null}
					</div>
				) : (
					<button
						type="button"
						className="item-entry__toggle"
						aria-expanded={open}
						aria-controls={open ? bodyId : undefined}
						// Named for what it shows (the entry and its summary), so the visible
						// words are in its name; `aria-expanded` says whether it is open.
						aria-label={summary ? `${name}: ${summary}` : name}
						onClick={() => onToggle(key)}
					>
						<Chevron className="item-entry__chevron" aria-hidden="true" />
						<span className="item-entry__ordinal" aria-hidden="true">{ordinal}</span>
						<span className={`item-entry__summary${summary ? '' : ' item-entry__summary--empty'}`}>
							{summary || `Empty ${nounsOf(group).singular}`}
						</span>
						{failing ? <span className="item-entry__flag">Needs attention</span> : null}
					</button>
				)}
				<EntryActions
					entry={entry}
					name={name}
					index={index}
					ids={NO_IDS}
					reordering={false}
					writable={writable}
					onMove={onMove}
					onRemove={onRemove}
					onCopyLink={onCopyLink}
				/>
			</div>
			{errors[ENTRY_COLUMN] ? (
				// A sentence about the whole entry (it was removed since, a refusal that
				// named no visible column): in the entry itself, folded or not.
				<div className="element-field__error item-entry__entry-error" role="alert">{errors[ENTRY_COLUMN]}</div>
			) : null}
			{open && !heading ? (
				<div className="item-entry__body" id={bodyId}>
					<div className="item-entry__columns">
						{columns.map(column => {
							const seeOnly = isSeeOnlyElement(column);
							return (
								<div
									key={column.name}
									className={`item-entry__cell item-entry__cell--${widthOf(column)}`}
									data-access={seeOnly ? 'see' : undefined}
								>
									<EventFormElement
										event={event}
										element={column}
										value={entry.elements?.[column.name]}
										onChange={handleColumn}
										cardMeta={cardMeta}
										showIconBadge={false}
										errorMessage={errors[column.name] || null}
										siblingDefs={columns}
										storedElements={storedElements}
										entryFile={column.dataType === 'file' ? entryFile : null}
										groupColumn={groupContext && isFormulaDriven(column) ? groupContext : null}
										disabled={!writable || seeOnly}
										attachmentsOn={attachmentsOn}
										idPrefix={idPrefix}
										zone={zone}
										resolveLookupValue={resolveLookupValue}
									/>
								</div>
							);
						})}
					</div>
				</div>
			) : null}
		</div>
	);
});

/** The compact row an entry folds to while reordering: grip, number, summary, ⋯. */
function ReorderRow({ handle, entry, ordinal, format, group, name, index, ids, onMove }) {
	const heading = isHeading(entry);
	const summary = entrySummary(group, entry, format);
	return (
		<div className={`item-entry item-entry--row${heading ? ' item-entry--heading' : ''}`} data-entry-key={entryKey(entry)}>
			{handle}
			{heading ? null : <span className="item-entry__ordinal" aria-hidden="true">{ordinal}</span>}
			<span className={`item-entry__summary${summary ? '' : ' item-entry__summary--empty'}`}>
				{summary || (heading ? 'Untitled heading' : `Empty ${nounsOf(group).singular}`)}
			</span>
			<EntryActions
				entry={entry}
				name={name}
				index={index}
				ids={ids}
				reordering
				writable
				onMove={onMove}
				onRemove={() => {}}
				onCopyLink={() => {}}
			/>
		</div>
	);
}

export default function RepeatingGroupField({
	group,
	value,
	onChange,
	item = null,
	cardMeta = null,
	storedElements = null,
	disabled = false,
	errors = null,
	groupError = null,
	zone = undefined,
	attachmentsOn = null,
	resolveLookupValue = null,
	resetKey = 0,
}) {
	const { message } = App.useApp();
	const labelId = `item-entries-${useId().replace(/:/g, '')}`;
	const entries = useMemo(() => entriesOf(value), [value]);
	const columns = useMemo(() => visibleColumns(group), [group]);
	const { max } = limitsOf(group);
	const counts = countEntries(entries);
	const writable = isGroupWritable(group) && !disabled;
	// A calculated column is read in its entry's scope (KPR-828): the entry, the
	// group's columns, and the item's values its formulas read — held to one
	// object while none of THOSE changes, so a keystroke in an unrelated element
	// does not re-render every entry of a long group.
	const formulaReads = useMemo(() => formulaReadsOf(columns), [columns]);
	const parentValuesRef = useRef({});
	const parentValues = useMemo(() => {
		if (!formulaReads) return null;
		const next = {};
		formulaReads.forEach((name) => {
			const held = item?.elements?.[name];
			// The record's own timestamps are inputs too (an element of that name wins).
			next[name] = held !== undefined ? held : (name === 'createdAt' || name === 'updatedAt' ? item?.[name] : undefined);
		});
		const before = parentValuesRef.current;
		const same = Object.keys(next).length === Object.keys(before).length && Object.keys(next).every(key => Object.is(next[key], before[key]));
		if (same) return before;
		parentValuesRef.current = next;
		return next;
	}, [formulaReads, item]);
	const groupContext = useMemo(() => (parentValues ? { group, columns, parentValues } : null), [parentValues, group, columns]);
	const headings = allowsHeadings(group);
	const atMax = counts.total >= max;
	const nouns = nounsOf(group);
	const label = group.label?.singular && group.label?.plural ? group.label.plural : (group.label?.singular || group.name);

	// The newest list and callbacks, read by handlers that must keep one
	// identity so the entries (memoised) do not re-render with every keystroke.
	const entriesRef = useRef(entries);
	entriesRef.current = entries;
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;
	const commit = useCallback((next) => {
		entriesRef.current = next;
		// An item that holds none reads '' (the API's own spelling).
		onChangeRef.current(group.name, next.length ? next : '');
	}, [group.name]);

	// ---- what is open, linked, focused, removed --------------------------------
	const [reordering, setReordering] = useState(false);
	const [openOverride, setOpenOverride] = useState({});
	const [focusKey, setFocusKey] = useState(null);
	const [lastRemoved, setLastRemoved] = useState(null);
	// The same removal, for the handlers: Undo changes the form, and a state
	// updater must stay pure (React runs it twice in development).
	const lastRemovedRef = useRef(null);
	const rememberRemoved = useCallback((removed) => { lastRemovedRef.current = removed; setLastRemoved(removed); }, []);
	const foldAll = entries.length > FOLD_ABOVE;
	// The form started over for another item: what was removed from the last one
	// cannot be undone into this one (its uploads were let go with the save).
	const seenResetRef = useRef(resetKey);
	useEffect(() => {
		if (seenResetRef.current === resetKey) return;
		seenResetRef.current = resetKey;
		rememberRemoved(null);
	}, [resetKey, rememberRemoved]);

	// A deep link to one entry (#entry-<id>) opens it, scrolls to it and marks it.
	const linkedId = useLinkedEntry(entries);
	const linkedPresent = Boolean(linkedId);

	useEffect(() => {
		if (reordering && (!writable || entries.length < 2)) setReordering(false);
	}, [reordering, writable, entries.length]);

	// ---- errors, per entry ------------------------------------------------------
	const mine = useMemo(() => errorsForGroup(errors, group.name), [errors, group.name]);
	const cacheRef = useRef(new Map());
	const errorsByEntry = useMemo(() => {
		const grouped = new Map();
		Object.keys(mine).forEach(name => {
			const dot = name.indexOf('.');
			if (dot < 0) return;
			const key = name.slice(0, dot);
			if (!grouped.has(key)) grouped.set(key, {});
			grouped.get(key)[name.slice(dot + 1)] = mine[name];
		});
		const next = new Map();
		grouped.forEach((found, key) => {
			const before = cacheRef.current.get(key);
			next.set(key, before && sameErrors(before, found) ? before : found);
		});
		cacheRef.current = next;
		return next;
	}, [mine]);
	const failingFirst = useMemo(() => (Object.keys(mine).length ? firstEntryError(group, entries, errors) : null), [mine, group, entries, errors]);

	// The first entry with something to fix takes the keyboard (the form
	// scrolls to the group; the group goes the last step).
	const focusedErrorRef = useRef(null);
	useEffect(() => {
		if (!failingFirst) { focusedErrorRef.current = null; return; }
		const token = `${failingFirst.key}.${failingFirst.column}.${Object.keys(mine).length}`;
		if (focusedErrorRef.current === token) return;
		focusedErrorRef.current = token;
		const frame = window.requestAnimationFrame?.(() => {
			const root = document.querySelector(`[data-entry-key="${failingFirst.key}"]`);
			if (!root) return;
			root.scrollIntoView?.({ block: 'center' });
			const bad = root.querySelector('.element-field--error');
			(firstControl(bad) || firstControl(root))?.focus?.({ preventScroll: true });
		});
		return () => { if (frame) window.cancelAnimationFrame?.(frame); };
	}, [failingFirst, mine]);

	// ---- stored (the echo) ------------------------------------------------------
	const storedById = useMemo(() => {
		const list = storedElements ? entriesOf(storedElements[group.name]) : [];
		return new Map(list.map(entry => [entry.id, entry]));
	}, [storedElements, group.name]);

	// ---- handlers ---------------------------------------------------------------
	const isOpen = useCallback((key, errored) => {
		if (errored) return true;
		if (openOverride[key] !== undefined) return openOverride[key];
		return !foldAll;
	}, [openOverride, foldAll]);

	const handleToggle = useCallback((key) => {
		setOpenOverride(prev => {
			const current = prev[key] !== undefined ? prev[key] : !foldAll;
			return { ...prev, [key]: !current };
		});
	}, [foldAll]);

	const handleColumn = useCallback((key, columnName, next) => {
		const list = entriesRef.current;
		commit(list.map(entry => (entryKey(entry) === key
			? {
				...withKey(entry),
				// A function is an update of the column's value as it stands now (a file
				// that landed after its entry was folded away, FileUploadField).
				elements: { ...(entry.elements || {}), [columnName]: typeof next === 'function' ? next(entry.elements?.[columnName]) : next },
			}
			: entry)));
	}, [commit]);

	const handleHeading = useCallback((key, text) => {
		commit(entriesRef.current.map(entry => (entryKey(entry) === key ? { ...withKey(entry), section: text } : entry)));
	}, [commit]);

	const addOne = useCallback((made) => {
		const list = entriesRef.current;
		if (list.length >= limitsOf(group).max) return;
		const key = entryKey(made);
		setOpenOverride(prev => ({ ...prev, [key]: true }));
		setFocusKey(key);
		rememberRemoved(null);
		commit([...list, made]);
	}, [commit, group, rememberRemoved]);

	const handleAdd = useCallback(() => addOne(newEntry(group, { zone })), [addOne, group, zone]);
	const handleAddHeading = useCallback(() => addOne(newHeading()), [addOne]);

	const handleRemove = useCallback((entry) => {
		const list = entriesRef.current;
		const key = entryKey(entry);
		const index = list.findIndex(item => entryKey(item) === key);
		if (index < 0) return;
		const name = entryName(group, list, list[index]);
		rememberRemoved({ entry: list[index], index, name });
		commit(list.filter((_, at) => at !== index));
	}, [commit, group, rememberRemoved]);

	const handleUndo = useCallback(() => {
		const prev = lastRemovedRef.current;
		if (!prev) return;
		const list = entriesRef.current;
		if (list.length >= limitsOf(group).max) return;
		const next = [...list];
		next.splice(Math.min(prev.index, next.length), 0, prev.entry);
		rememberRemoved(null);
		commit(next);
		setFocusKey(entryKey(prev.entry));
	}, [commit, group, rememberRemoved]);

	const handleOrder = useCallback((ids) => {
		const byKey = new Map(entriesRef.current.map(entry => [entryKey(entry), entry]));
		const next = ids.map(id => byKey.get(id)).filter(Boolean);
		if (next.length === entriesRef.current.length) commit(next);
	}, [commit]);

	// The item behind a file column's stored files: only its id and its `files`
	// facts, so an entry (memoised) does not re-render with every keystroke.
	const fileItemId = item?._id;
	const fileItemFiles = item?.files;
	const fileItem = useMemo(() => ({ _id: fileItemId, files: fileItemFiles }), [fileItemId, fileItemFiles]);

	const itemIdForLinks = normalizeId(item?._id);
	const collectionIdForLinks = normalizeId(item?.collection_id || item?.collectionId);
	const handleCopyLink = useCallback(async (entry) => {
		const link = entryLink(collectionIdForLinks, itemIdForLinks, entry?.id);
		if (!link) return;
		const ok = await copyTextToClipboard(link);
		if (ok) message.success('Link copied.'); else message.error('Could not copy the link.');
	}, [message, itemIdForLinks, collectionIdForLinks]);

	// A new entry takes focus in its first column.
	useEffect(() => {
		if (!focusKey) return;
		const root = document.querySelector(`[data-entry-key="${focusKey}"]`);
		if (!root) return;
		root.scrollIntoView?.({ block: 'nearest' });
		firstControl(root)?.focus?.({ preventScroll: true });
		setFocusKey(null);
	}, [focusKey, entries]);

	// ---- reading ----------------------------------------------------------------
	const format = useCallback((column, raw) => formatValue(column, raw, { resolveLookupValue }), [resolveLookupValue]);
	const ids = useMemo(() => entries.map(entryKey), [entries]);
	const ordinals = useMemo(() => {
		let n = 0;
		return entries.map(entry => (isHeading(entry) ? 0 : ++n));
	}, [entries]);
	const names = useMemo(() => entries.map((entry, i) => (isHeading(entry)
		? (entry.section ? `heading ${entry.section}` : 'heading')
		: `${nouns.singular} ${ordinals[i]}`)), [entries, ordinals, nouns.singular]);

	const collectionId = normalizeId(item?.collection_id || item?.collectionId) || undefined;
	const cardId = normalizeId(item?.card_id || cardMeta?._id) || undefined;
	const seeOnlyNames = seeOnlyColumnNames(group);
	const help = elementHelpText(group);
	const groupMessage = groupError || null;
	const belowMin = counts.real < (limitsOf(group).min || 0);

	return (
		<div
			id={`element-field-${group.name}`}
			className={`element-field item-entries${groupMessage ? ' item-entries--invalid' : ''}`}
			role="group"
			aria-labelledby={labelId}
			data-testid="repeating-group"
		>
			<div className="item-entries__head">
				<div className="item-entries__title">
					<FieldHelp text={help} label={label}>
						<span id={labelId} className="item-entries__label">{label}</span>
					</FieldHelp>
					<span className="item-entries__count" aria-label={`${counts.total} of ${max} entries`}>
						{counts.total} of {max}
					</span>
				</div>
				{writable && entries.length >= 2 ? (
					<ReorderButton
						active={reordering}
						what={label.toLowerCase()}
						onClick={() => setReordering(on => !on)}
					/>
				) : null}
			</div>
			<p className={`item-entries__hint form-panel__hint${belowMin && entries.length ? ' item-entries__hint--short' : ''}`}>
				{limitsSentence(group)}
				{belowMin && entries.length ? ` ${belowMinSentence(group, counts.real)}` : ''}
			</p>
			{groupMessage ? <div className="element-field__error item-entries__error" role="alert">{groupMessage}</div> : null}
			{seeOnlyNames.length && writable ? (
				<p className="form-panel__hint item-entries__note">
					{seeOnlyNames.join(', ')} {seeOnlyNames.length === 1 ? 'is' : 'are'} read-only here. {GRANT_READ_ONLY_REASON}
				</p>
			) : null}

			{entries.length === 0 ? (
				<div className="item-entries__empty" data-testid="repeating-group-empty">
					<p className="item-entries__empty-title">No {nouns.plural} yet.</p>
					{writable ? (
						<>
							<p className="item-entries__empty-text">
								{limitsOf(group).min > 0
									? `Add the first ${nouns.singular}. It needs at least ${countPhrase(group, limitsOf(group).min)}.`
									: `Add the first ${nouns.singular}.`}
							</p>
							<Button icon={<PLUS aria-hidden="true" />} onClick={handleAdd}>{addLabel(group)}</Button>
						</>
					) : null}
				</div>
			) : reordering ? (
				<div
					className="item-entries__reorder"
					// Esc ends a keyboard drag; it must not also Cancel the form.
					onKeyDownCapture={event => {
						if (event.key === 'Escape' && event.target?.closest?.('[aria-pressed="true"]')) event.preventDefault();
					}}
				>
					<SortableList
						items={entries.map((entry, i) => ({ id: ids[i], entry, i }))}
						getLabel={row => names[row.i]}
						onReorder={handleOrder}
						ariaLabel={`Reorder ${label.toLowerCase()}`}
						reordering
						renderItem={(row, { handle }) => (
							<ReorderRow
								handle={handle}
								entry={row.entry}
								ordinal={ordinals[row.i]}
								format={format}
								group={group}
								name={names[row.i]}
								index={row.i}
								ids={ids}
								onMove={handleOrder}
							/>
						)}
					/>
				</div>
			) : (
				<ol className="item-entries__list" aria-label={label}>
					{entries.map((entry, i) => {
						const key = ids[i];
						const entryErrors = errorsByEntry.get(key) || NO_ERRORS;
						return (
							<li key={key} className="item-entries__item">
								<GroupEntry
									group={group}
									columns={columns}
									entry={entry}
									index={i}
									ordinal={ordinals[i]}
									name={names[i]}
									open={isOpen(key, Object.keys(entryErrors).length > 0) || (linkedPresent && entry.id === linkedId)}
									linked={linkedPresent && entry.id === linkedId}
									errors={entryErrors}
									storedEntry={entry.id ? storedById.get(entry.id) : null}
									groupContext={groupContext}
									fileItem={fileItem}
									writable={writable}
									collectionId={collectionId}
									cardId={cardId}
									cardMeta={cardMeta}
									attachmentsOn={attachmentsOn}
									zone={zone}
									resolveLookupValue={resolveLookupValue}
									format={format}
									onToggle={handleToggle}
									onColumn={handleColumn}
									onHeading={handleHeading}
									onMove={handleOrder}
									onRemove={handleRemove}
									onCopyLink={handleCopyLink}
								/>
							</li>
						);
					})}
				</ol>
			)}

			<div className="item-entries__status" role="status" aria-live="polite">
				{lastRemoved ? (
					<span className="item-entries__undo">
						<span>Removed {lastRemoved.name}.</span>
						<Button
							type="link"
							size="small"
							icon={<ARROW_COUNTERCLOCKWISE aria-hidden="true" />}
							onClick={handleUndo}
							disabled={atMax}
						>
							Undo
						</Button>
						<Button
							type="text"
							size="small"
							icon={<CLOSE aria-hidden="true" />}
							aria-label="Dismiss"
							onClick={() => rememberRemoved(null)}
						/>
					</span>
				) : null}
			</div>

			{writable && !reordering && entries.length > 0 ? (
				<div className="item-entries__add">
					<Button icon={<PLUS aria-hidden="true" />} onClick={handleAdd} disabled={atMax} aria-describedby={atMax ? `${labelId}-max` : undefined}>
						{addLabel(group)}
					</Button>
					{headings ? (
						<Button type="default" onClick={handleAddHeading} disabled={atMax}>Add heading</Button>
					) : null}
					{atMax ? <span id={`${labelId}-max`} className="item-entries__max">{atMaxSentence(group)}</span> : null}
				</div>
			) : null}
			{writable && entries.length === 0 && headings ? (
				<div className="item-entries__add item-entries__add--solo">
					<Button type="default" onClick={handleAddHeading}>Add heading</Button>
				</div>
			) : null}
		</div>
	);
}

export { GroupEntry };
